import React, { useState, useRef, useEffect, PropsWithChildren, useCallback, useMemo } from 'react';
import {
  Box,
  Chip,
  Tooltip,
  IconButton,
  useTheme,
  Typography,
  alpha,
  Badge,
  Popover,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  Grid,
  Paper,
  Divider,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  InputAdornment,
  Collapse,
  Autocomplete,
  ListSubheader,
  CircularProgress,
  useMediaQuery
} from '@mui/material';
import {
  NavigateBefore as NavigateBeforeIcon,
  NavigateNext as NavigateNextIcon,
  PriorityHigh as PriorityHighIcon,
  Groups as GroupsIcon,
  EditNote as EditNoteIcon,
  AutoMode as AutoModeIcon,
  PauseCircleOutline as PauseCircleOutlineIcon,
  DoneAll as DoneAllIcon,
  Drafts as DraftsIcon,
  HourglassEmpty as HourglassEmptyIcon,
  ErrorOutline as ErrorOutlineIcon,
  Forum as ForumIcon,
  AllInbox as AllInboxIcon,
  Archive as ArchiveIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  DateRange as DateRangeIcon,
  Memory as MemoryIcon,
  Settings as SettingsIcon,
  Search as SearchIcon,
  PersonOff as PersonOffIcon,
  Code as CodeIcon,
  Clear as ClearIcon,
  Add as AddIcon,
  Star as StarIcon
} from '@mui/icons-material';
import { filterConversations } from '../../utils/conversationUtils';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useAppDispatch, useAppSelector } from 'src/store';
import ContactsSearchAutocomplete from '../Contacts/ContactsSearchAutocomplete';
import { fetchFilteredConversations, setFilter, selectIsFilterActive } from 'src/features/account/filteredConversations.slice';
import { selectHasMore, selectLimit, setLimit as reduxSetLimit } from 'src/features/account/conversations.slice';
import { useSnackbar } from 'notistack';
// Filter options with their properties
const filterOptions = [
  {
    id: 'any',
    label: 'All',
    icon: <AllInboxIcon />,
    color: 'default',
    tooltip: 'All conversations',
    emoji: '📥'
  },
  {
    id: 'all_active',
    label: 'All Active',
    icon: <AllInboxIcon />,
    color: 'default',
    tooltip: 'Active conversations',
    emoji: '📥'
  },
  {
    id: 'starred',
    label: 'Starred',
    icon: <StarIcon />,
    color: 'warning',
    tooltip: 'Starred',
    emoji: '⭐'
  },
  {
    id: 'priority',
    label: 'Priority',
    icon: <PriorityHighIcon />,
    color: 'error',
    tooltip: 'Error or needs attention',
    emoji: '🔴'
  },
  {
    id: 'engaged_users',
    label: 'Engaged Users',
    icon: <GroupsIcon />,
    color: 'success',
    tooltip: 'Engaged users',
    emoji: '👥'
  },
  {
    id: 'draft_messages',
    label: 'Draft Messages',
    icon: <DraftsIcon />,
    color: 'warning',
    tooltip: 'Pending delivery',
    emoji: '📝'
  },
  {
    id: 'draft_conversations',
    label: 'Draft Convos',
    icon: <EditNoteIcon />,
    color: 'info',
    tooltip: 'No delivered messages',
    emoji: '✏️'
  },
  {
    id: 'scheduled_delivery',
    label: 'Scheduled Delivery',
    icon: <HourglassEmptyIcon />,
    color: 'info',
    tooltip: 'Scheduled delivery',
    emoji: '⏳'
  },
  {
    id: 'manual_standby',
    label: 'Manual Mode',
    icon: <HourglassEmptyIcon />,
    color: 'secondary',
    tooltip: 'Manual/standby',
    emoji: '⏳'
  },
  {
    id: 'awaiting_reply',
    label: 'Awaiting User',
    icon: <ForumIcon />,
    color: 'warning',
    tooltip: 'Awaiting user',
    emoji: '💬'
  },
  {
    id: 'closed',
    label: 'Closed',
    icon: <ArchiveIcon />,
    color: 'default',
    tooltip: 'Closed',
    emoji: '🗃️'
  },
  {
    id: 'pending_follow_up_within_12_hours',
    label: 'Pending Follow-Up (12h)',
    icon: <HourglassEmptyIcon />,
    color: 'info',
    tooltip: 'Follow-up within 12h',
    emoji: '⏳'
  },
  // New datetime range options
  {
    id: 'today',
    label: 'Today',
    icon: <DateRangeIcon />,
    color: 'primary',
    tooltip: 'Conversations from today',
    emoji: '📅',
    isDateRange: true
  },
  {
    id: 'this_week',
    label: 'This Week',
    icon: <DateRangeIcon />,
    color: 'primary',
    tooltip: 'Conversations from this week',
    emoji: '📅',
    isDateRange: true
  },
  {
    id: 'this_month',
    label: 'This Month',
    icon: <DateRangeIcon />,
    color: 'primary',
    tooltip: 'Conversations from this month',
    emoji: '📅',
    isDateRange: true
  },
  {
    id: 'last_30_days',
    label: 'Last 30 Days',
    icon: <DateRangeIcon />,
    color: 'primary',
    tooltip: 'Conversations from last 30 days',
    emoji: '📅',
    isDateRange: true
  },
  {
    id: 'clear_filters',
    label: 'Clear Filters',
    icon: <ClearIcon />,
    color: 'default',
    tooltip: 'Clear filters',
    emoji: '❌'
  }
];

const TIME_PRESETS = [
  { value: 'last_24h', label: 'Last 24 Hours' },
  { value: 'last_week', label: 'Last Week' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'last_3_months', label: 'Last 3 Months' },
  { value: 'custom', label: 'Custom Date Range' }
];

const DATE_FIELD_OPTIONS = [
  { value: 'last_user_message_received_at_before', label: 'Last User Message Before' },
  { value: 'last_comms_from_contact_after', label: 'Last Comms From Contact After' },
  { value: 'last_sms_from_contact_after', label: 'Last SMS From Contact After' },
  { value: 'last_email_from_contact_after', label: 'Last Email From Contact After' },
  { value: 'last_chat_from_contact_after', label: 'Last Chat From Contact After' },
  { value: 'last_phone_call_from_contact_after', label: 'Last Phone Call From Contact After' },
  { value: 'conversation_latest_delivery_at_before', label: 'Latest Delivery Before' },
  { value: 'conversation_latest_message_created_at_before', label: 'Latest Message Created Before' },
  { value: 'conversation_last_updated_before', label: 'Last Updated Before' }
];

const statusOptions = [
  "All",
  "Active",
  "Closed",
  "Error",
  "Awaiting Oppy",
  "Awaiting User",
  "Processing Reply",
  "Standby",
  "Needs Attention",
  "Draft",
  "Has Draft Messages (Pending Delivery)"
];

interface AdvancedFilters {
  channel_id?: string;
  user_not_responded_to?: boolean;
  has_draft_messages?: boolean;
  dateField?: string;
  dateValue?: Date | null;
  timePreset?: string;
  customDateStart?: Date | null;
  customDateEnd?: Date | null;
  status?: string;
  contactId?: string;
  queryString?: string;
  conversationLatestDeliveryAtBefore?: Date | null;
  conversationLatestMessageCreatedAtBefore?: Date | null;
  conversationLastUpdatedBefore?: Date | null;
  limit?: number;
  messagesLimit?: number;
  communication_method?: string;
}

interface ConversationFiltersProps {
  selectedFilter: string;
  onFilterChange: (filterId: string) => void;
  conversations: any[]; // Add conversations prop to calculate counts
  onAdvancedFilterChange?: (filters: Record<string, any>) => void;
  channels?: any[]; // Available channels for filtering
}

const ConversationFilters = ({
  selectedFilter,
  onFilterChange,
  conversations = [],
  onAdvancedFilterChange,
  channels = []
}: ConversationFiltersProps): JSX.Element => {
  const theme = useTheme();
  const darkMode = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery('(max-width:899px)');
  const dispatch = useAppDispatch();
  const isFilterActive = useAppSelector(selectIsFilterActive);
  const reduxLimit = useAppSelector(selectLimit);
  const hasMore = useAppSelector(selectHasMore);
  const { enqueueSnackbar } = useSnackbar();

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);
  const [loadingMore, setLoadingMore] = useState<Record<string, boolean>>({});
  const [isLoadMoreMode, setIsLoadMoreMode] = useState(false);
  const [activeLoadMoreFilter, setActiveLoadMoreFilter] = useState<string | null>(null);
  const [filterCounts, setFilterCounts] = useState<Record<string, number>>({});
  
  // Refs for search debouncing and request management
  const searchDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const searchRequestIdRef = useRef<number>(0);
  const latestRequestIdRef = useRef<number>(0);

  // Advanced filters
  const [advancedFiltersAnchorEl, setAdvancedFiltersAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [advancedFilters, setAdvancedFilters] = useState<AdvancedFilters>({});
  const [showMemoryEditor, setShowMemoryEditor] = useState(false);
  const [showDateSelector, setShowDateSelector] = useState(false);
  const [showChannelSelector, setShowChannelSelector] = useState(false);
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);

  // From ConversationsListFilterBar
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedContactId, setSelectedContactId] = useState("");
  const [subject, setSubject] = useState("");
  const [queryString, setQueryString] = useState("");
  const [latestDeliveryBefore, setLatestDeliveryBefore] = useState<Date | null>(null);
  const [latestMessageCreatedBefore, setLatestMessageCreatedBefore] = useState<Date | null>(null);
  const [lastUpdatedBefore, setLastUpdatedBefore] = useState<Date | null>(null);
  const [limit, setLimit] = useState(reduxLimit);
  const [messagesLimit, setMessagesLimit] = useState(1);
  const [timeFilter, setTimeFilter] = useState<string>('');

  // Update filter counts whenever conversations change
  useEffect(() => {
    const newFilterCounts = filterOptions.reduce((counts, option) => {
      if (option.id === 'priority') {
        // Calculate priority conversations count inline to avoid dependency issues
        const priorityCount = conversations.filter(conv => 
          conv.status === 'error' || conv.status === 'needs_attention'
        ).length;
        counts[option.id] = priorityCount;
      } else {
        const filteredConvos = filterConversations(conversations, option.id);
        counts[option.id] = filteredConvos.length;
      }
      return counts;
    }, {} as Record<string, number>);

    setFilterCounts(newFilterCounts);
  }, [conversations]);

  // Check if any advanced filters are applied
  const hasAdvancedFilters = Object.values(advancedFilters).some(
    value => value !== undefined && value !== '' && value !== null && value !== false
  );

  // Check if scrolling is needed and update arrow visibility
  const checkScrollPosition = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 5); // 5px buffer
    }
  };

  // Initialize scroll check
  useEffect(() => {
    checkScrollPosition();
    // Add resize listener to check scroll arrows on window resize
    window.addEventListener('resize', checkScrollPosition);
    return () => window.removeEventListener('resize', checkScrollPosition);
  }, []);

  // Sync local limit state with Redux
  useEffect(() => {
    setLimit(reduxLimit);
  }, [reduxLimit]);

  // Cleanup effect - cancel requests on unmount
  useEffect(() => {
    return () => {
      // Clear any pending debounce timer
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = null;
      }
    };
  }, []);

  // Scroll left
  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -200, behavior: 'smooth' });
      // After scrolling, check position again
      setTimeout(checkScrollPosition, 300);
    }
  };

  // Scroll right
  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 200, behavior: 'smooth' });
      // After scrolling, check position again
      setTimeout(checkScrollPosition, 300);
    }
  };

  // Handle advanced filters dialog
  const handleOpenAdvancedFilters = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAdvancedFiltersAnchorEl(event.currentTarget);

    // Initialize advancedFilters with current values from state
    setAdvancedFilters({
      status: selectedStatus,
      contactId: selectedContactId,
      queryString,
      conversationLatestDeliveryAtBefore: latestDeliveryBefore,
      conversationLatestMessageCreatedAtBefore: latestMessageCreatedBefore,
      conversationLastUpdatedBefore: lastUpdatedBefore,
      limit,
      messagesLimit,
      ...advancedFilters // Keep any other existing advanced filters
    });

    // If on mobile and there's a query string, expand the search field
    if (isMobile && queryString) {
      setIsSearchExpanded(true);
    }
  };

  const handleCloseAdvancedFilters = () => {
    setAdvancedFiltersAnchorEl(null);
    setIsSearchExpanded(false); // Reset search expansion on mobile
  };

  // Helper for date presets
  const getDateFromPreset = (preset: string): Date | null => {
    const now = new Date();

    switch (preset) {
      case 'last_24h':
        return new Date(now.getTime() - 24 * 60 * 60 * 1000);
      case 'last_week':
        return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      case 'last_month':
        return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      case 'last_3_months':
        return new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
      default:
        // Handle month filters (e.g., "2024-01" for January 2024)
        if (typeof preset === 'string' && preset.match(/^\d{4}-\d{2}$/)) {
          const [year, month] = preset.split('-').map(Number);
          return new Date(year, month - 1, 1);
        }
        return null;
    }
  };

  const handleTimeFilterChange = (e: { target: { value: string } }) => {
    const value = e.target.value;
    setTimeFilter(value);
    setLatestDeliveryBefore(getDateFromPreset(value));

    // Update advancedFilters with the new time filter
    setAdvancedFilters(prev => ({
      ...prev,
      timePreset: value,
      conversationLatestDeliveryAtBefore: getDateFromPreset(value)
    }));
  };

  // Generate past 12 months options
  const getMonthOptions = () => {
    const months = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push({
        value: date.toISOString().slice(0, 7),
        label: date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' })
      });
    }
    return months;
  };

  const handleStatusSelect = (event, newValue) => {
    setSelectedStatus(newValue);
    setAdvancedFilters(prev => ({ ...prev, status: newValue }));
  };

  // Apply advanced filters
  const applyAdvancedFilters = async () => {
    // Clear any pending debounce timer
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }

    // Convert status to backend format
    const statusFilter = (() => {
      switch (advancedFilters.status) {
        case "All": return "any";
        case "Active": return "all_active";
        case "Closed": return "closed";
        case "Error": return "error";
        case "Awaiting Oppy": return "pending_assistant_reply";
        case "Awaiting User": return "pending_user_reply";
        case "Processing Reply": return "processing_autocomplete";
        case "Standby": return "standby";
        case "Needs Attention": return "needs_attention";
        case "Draft": return "draft";
        case "Has Draft Messages (Pending Delivery)": return ""; // Empty as we'll use a different parameter
        default: return "";
      }
    })();

    // Update local state first
    const newLimit = advancedFilters.limit || 25;
    const newMessagesLimit = advancedFilters.messagesLimit || 1;

    setSelectedStatus(advancedFilters.status || "");
    setSelectedContactId(advancedFilters.contactId || "");
    setQueryString(advancedFilters.queryString || "");
    setLatestDeliveryBefore(advancedFilters.conversationLatestDeliveryAtBefore);
    setLatestMessageCreatedBefore(advancedFilters.conversationLatestMessageCreatedAtBefore);
    setLastUpdatedBefore(advancedFilters.conversationLastUpdatedBefore);
    setLimit(newLimit);
    setMessagesLimit(newMessagesLimit);

    const filterParams = {
      status: statusFilter,
      contactId: advancedFilters.contactId,
      universal_search: advancedFilters.queryString,
      conversationLatestDeliveryAtBefore: advancedFilters.conversationLatestDeliveryAtBefore,
      conversationLatestMessageCreatedAtBefore: advancedFilters.conversationLatestMessageCreatedAtBefore,
      conversationLastUpdatedBefore: advancedFilters.conversationLastUpdatedBefore,
      limit: newLimit,
      messagesLimit: newMessagesLimit,
      has_draft_messages: advancedFilters.status === "Has Draft Messages (Pending Delivery)" ? true : advancedFilters.has_draft_messages,
      channel_id: advancedFilters.channel_id,
      user_not_responded_to: advancedFilters.user_not_responded_to,
      communication_method: advancedFilters.communication_method,
    };

    // For custom date range handling
    if (advancedFilters.dateField && advancedFilters.timePreset === 'custom') {
      if (advancedFilters.customDateStart) {
        if (advancedFilters.dateField.includes('_before')) {
          filterParams[advancedFilters.dateField] = advancedFilters.customDateEnd || advancedFilters.customDateStart;
        } else if (advancedFilters.dateField.includes('_after')) {
          filterParams[advancedFilters.dateField] = advancedFilters.customDateStart;
        }
      }
    } else if (advancedFilters.dateField && advancedFilters.timePreset) {
      const dateValue = getDateFromPreset(advancedFilters.timePreset);
      if (dateValue) {
        filterParams[advancedFilters.dateField] = dateValue;
      }
    }

    // condense filterParams to only include non-null values
    const condensedFilterParams = Object.fromEntries(Object.entries(filterParams)
      .filter(([_, value]) => value !== null && value !== undefined && value !== ''));

    // Callback to parent component if provided
    if (onAdvancedFilterChange) {
      onAdvancedFilterChange(condensedFilterParams);
    }

    // Dispatch to Redux directly
    dispatch(setFilter(condensedFilterParams));
    dispatch(reduxSetLimit(newLimit)); // Update limit in conversations slice

    // Close the popover after applying filters
    handleCloseAdvancedFilters();

    // Show success notification
    enqueueSnackbar('Filters applied successfully!', { variant: 'success' });
  };

  const handleClearFilters = () => {
    // Reset local state
    setSelectedStatus("");
    setSelectedContactId("");
    setSubject("");
    setQueryString("");
    setLatestDeliveryBefore(null);
    setLatestMessageCreatedBefore(null);
    setLastUpdatedBefore(null);

    // Default values for pagination
    const defaultLimit = 25;
    const defaultMessagesLimit = 1;

    setLimit(defaultLimit);
    setMessagesLimit(defaultMessagesLimit);
    setTimeFilter("");

    // Reset advanced filters
    setAdvancedFilters({});
    onFilterChange('any');

    // Reset load more mode
    setIsLoadMoreMode(false);
    setActiveLoadMoreFilter(null);

    // Dispatch to Redux with default limit values
    dispatch(setFilter({ limit: defaultLimit, messagesLimit: defaultMessagesLimit }));
    dispatch(reduxSetLimit(defaultLimit)); // Update limit in conversations slice
    // dispatch(fetchFilteredConversations({ limit: defaultLimit, messagesLimit: defaultMessagesLimit }));

    // Callback to parent component if provided
    if (onAdvancedFilterChange) {
      onAdvancedFilterChange({});
    }

    // Close the popover
    handleCloseAdvancedFilters();

    // Show success notification
    enqueueSnackbar('Filters cleared successfully!', { variant: 'success' });
  };

  // Handle change for advanced filter fields
  const handleAdvancedFilterChange = (field: keyof AdvancedFilters, value: any) => {
    setAdvancedFilters(prev => ({ ...prev, [field]: value }));
  };

  // Handle query string change with debouncing
  const handleQueryStringChange = useCallback((value: string) => {
    // Update the local state immediately for UI responsiveness
    handleAdvancedFilterChange('queryString', value);
    setQueryString(value);

    // Clear existing debounce timer
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }

    // Increment request ID to track the latest request
    const currentRequestId = ++searchRequestIdRef.current;
    latestRequestIdRef.current = currentRequestId;

    // If clearing the search, apply immediately without debounce
    if (!value.trim()) {
      const filterParams = {
        status: selectedStatus ? (() => {
          switch (selectedStatus) {
            case "All": return "any";
            case "Active": return "all_active";
            case "Closed": return "closed";
            case "Error": return "error";
            case "Awaiting Oppy": return "pending_assistant_reply";
            case "Awaiting User": return "pending_user_reply";
            case "Processing Reply": return "processing_autocomplete";
            case "Standby": return "standby";
            case "Needs Attention": return "needs_attention";
            case "Draft": return "draft";
            default: return "";
          }
        })() : "",
        contactId: selectedContactId,
        universal_search: '',
        conversationLatestDeliveryAtBefore: latestDeliveryBefore,
        conversationLatestMessageCreatedAtBefore: latestMessageCreatedBefore,
        conversationLastUpdatedBefore: lastUpdatedBefore,
        limit: limit,
        messagesLimit: messagesLimit,
        has_draft_messages: selectedStatus === "Has Draft Messages (Pending Delivery)" ? true : undefined,
      };

      // condense filterParams to only include non-null values
      const condensedFilterParams = Object.fromEntries(Object.entries(filterParams)
        .filter(([_, val]) => val !== null && val !== undefined && val !== ''));

      dispatch(setFilter(condensedFilterParams));
      dispatch(fetchFilteredConversations(condensedFilterParams));
      return;
    }

    // Debounce the actual search for non-empty values
    searchDebounceRef.current = setTimeout(() => {
      // Check if this is still the latest request
      if (currentRequestId !== latestRequestIdRef.current) {
        return; // Skip if a newer request has been initiated
      }

      const filterParams = {
        status: selectedStatus ? (() => {
          switch (selectedStatus) {
            case "All": return "any";
            case "Active": return "all_active";
            case "Closed": return "closed";
            case "Error": return "error";
            case "Awaiting Oppy": return "pending_assistant_reply";
            case "Awaiting User": return "pending_user_reply";
            case "Processing Reply": return "processing_autocomplete";
            case "Standby": return "standby";
            case "Needs Attention": return "needs_attention";
            case "Draft": return "draft";
            default: return "";
          }
        })() : "",
        contactId: selectedContactId,
        universal_search: value.trim(),
        conversationLatestDeliveryAtBefore: latestDeliveryBefore,
        conversationLatestMessageCreatedAtBefore: latestMessageCreatedBefore,
        conversationLastUpdatedBefore: lastUpdatedBefore,
        limit: limit,
        messagesLimit: messagesLimit,
        has_draft_messages: selectedStatus === "Has Draft Messages (Pending Delivery)" ? true : undefined,
      };

      // condense filterParams to only include non-null values
      const condensedFilterParams = Object.fromEntries(Object.entries(filterParams)
        .filter(([_, val]) => val !== null && val !== undefined && val !== ''));

      // Apply the search filters
      dispatch(setFilter(condensedFilterParams));
      
      // Dispatch with request ID tracking
      dispatch(fetchFilteredConversations(condensedFilterParams))
        .unwrap()
        .then(() => {
          // Only process if this is still the latest request
          if (currentRequestId === latestRequestIdRef.current) {
            // Success - results will be handled by Redux
          }
        })
        .catch((error) => {
          // Only log error if this is still the latest request
          if (currentRequestId === latestRequestIdRef.current) {
            console.error('Search error:', error);
          }
        });
    }, 800); // 800ms debounce delay
  }, [dispatch, selectedStatus, selectedContactId, latestDeliveryBefore, 
      latestMessageCreatedBefore, lastUpdatedBefore, limit, messagesLimit]);

  const handleTimePresetChange = (preset: string) => {
    setAdvancedFilters(prev => ({
      ...prev,
      timePreset: preset,
      // Clear custom dates if not using custom preset
      ...(preset !== 'custom' ? { customDateStart: null, customDateEnd: null } : {})
    }));
  };

  const advancedFiltersOpen = Boolean(advancedFiltersAnchorEl);
  const advancedFiltersId = advancedFiltersOpen ? 'advanced-filters-popover' : undefined;

  // Filter out options with zero conversations
  const visibleFilterOptions = isLoadMoreMode
    ? [
      // In load more mode, only show the active filter and clear filters option
      ...filterOptions.filter(option => option.id === activeLoadMoreFilter || option.id === 'clear_filters')
    ]
    : filterOptions; // Show all filter options

  // Handle loading more conversations for a filter
  const handleLoadMore = async (e: React.MouseEvent, filterId: string) => {
    e.stopPropagation(); // Prevent the filter chip click event

    // Skip if already loading for this filter
    if (loadingMore[filterId]) return;

    // Enter load more mode with the selected filter
    setIsLoadMoreMode(true);
    setActiveLoadMoreFilter(filterId);
    let newLimit;
    if (limit === 10) {
      newLimit = limit + 15;
    } else if (limit === 100) {
      newLimit = limit;
    } else {
      newLimit = limit + 25;
    }

    // Prepare filter parameters based on the selected filter type
    const filterParams: {
      status?: string;
      limit: number;
      messagesLimit: number;
      has_draft_messages?: boolean;
      user_not_responded_to?: boolean;
      delivery_state?: string;
      autocomplete_status?: string;
      memory_contains?: any;
      has_engaged_users?: boolean;
      has_scheduled_delivery?: boolean;
      is_priority?: boolean;
      starred?: boolean;
      conversationLatestMessageCreatedAtAfter?: Date;
    } = {
      limit: newLimit, // Fetch more conversations
      messagesLimit
    };

    // Map the filter ID to the correct API parameters based on the filter implementation in utils/conversationUtils.ts
    switch (filterId) {
      case 'priority':
        filterParams.is_priority = true;
        break;
      case 'starred':
        filterParams.starred = true;
        break;
      case 'engaged_users':
        filterParams.has_engaged_users = true;
        break;
      case 'draft_messages':
        filterParams.has_draft_messages = true;
        break;
      case 'draft_conversations':
        filterParams.status = 'draft';
        break;
      case 'scheduled_delivery':
        filterParams.has_scheduled_delivery = true;
        break;
      case 'pending_follow_up_within_12_hours':
        filterParams.has_draft_messages = true;
        break;
      case 'auto_reply_off':
        filterParams.delivery_state = 'pause';
        break;
      case 'manual_standby':
        filterParams.status = 'standby';
        break;
      case 'awaiting_reply':
        filterParams.status = 'pending_user_reply,awaiting_reply';
        break;
      case 'closed':
        filterParams.status = 'closed';
        break;
      case 'today':
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        filterParams.conversationLatestMessageCreatedAtAfter = today;
        break;
      case 'this_week':
        const thisWeek = new Date();
        thisWeek.setDate(thisWeek.getDate() - thisWeek.getDay()); // Start of week
        thisWeek.setHours(0, 0, 0, 0);
        filterParams.conversationLatestMessageCreatedAtAfter = thisWeek;
        break;
      case 'this_month':
        const thisMonth = new Date();
        thisMonth.setDate(1); // First day of month
        thisMonth.setHours(0, 0, 0, 0);
        filterParams.conversationLatestMessageCreatedAtAfter = thisMonth;
        break;
      case 'last_30_days':
        const last30Days = new Date();
        last30Days.setDate(last30Days.getDate() - 30);
        last30Days.setHours(0, 0, 0, 0);
        filterParams.conversationLatestMessageCreatedAtAfter = last30Days;
        break;
      case 'any':
      default:
        filterParams.status = '';
        break;
    }

    // Set loading state for this particular filter
    setLoadingMore(prev => ({ ...prev, [filterId]: true }));

    // Update the local limit state
    setLimit(newLimit);

    // Update limit in the conversations slice too
    dispatch(reduxSetLimit(newLimit));

    try {
      // Set the filter in the Redux store first
      dispatch(setFilter(filterParams));

      // Then dispatch the action to fetch more conversations
      const result = await dispatch(fetchFilteredConversations(filterParams)).unwrap();

      // Update the selected filter to the one we just loaded more for
      onFilterChange(filterId);

      // Scroll to conversation list container
      setTimeout(() => {
        const conversationListElement = document.querySelector('.conversations-list-container');
        if (conversationListElement) {
          conversationListElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }, 100);

      // Customize the success message based on filter type
      let successMessage = `Loaded ${result?.length - filterCounts[filterId] || 0} more conversations`;

      // Add specific messaging for certain filter types
      if (filterId === 'engaged_users') {
        successMessage = `Loaded ${result?.length - filterCounts[filterId] || 0} more conversations with engaged users`;
      } else if (filterId === 'scheduled_delivery') {
        successMessage = `Loaded ${result?.length - filterCounts[filterId] || 0} more conversations with scheduled delivery`;
      } else {
        // Default message with filter label
        successMessage = `Loaded ${result?.length - filterCounts[filterId] || 0} more conversations in "${filterOptions.find(opt => opt.id === filterId)?.label || filterId}" category`;
      }

      enqueueSnackbar(successMessage, {
        variant: 'success',
        autoHideDuration: 3000,
      });
    } catch (error) {
      console.error('Error loading more conversations:', error);
      // Show error notification
      enqueueSnackbar(`Error loading more conversations: ${error.message || 'Unknown error'}`, {
        variant: 'error',
        autoHideDuration: 5000,
      });
    } finally {
      // Reset loading state
      setLoadingMore(prev => ({ ...prev, [filterId]: false }));
    }
  };

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        mb: 0,
        pb: 0.5, // Add padding to prevent cut-off shadows
        bgcolor: theme.palette.background.glass,
      }}
    >
      {/* Left scroll button */}
      {showLeftArrow && (
        <Box
          sx={{
            position: 'absolute',
            left: -12,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            background: darkMode
              ? `linear-gradient(to right, ${alpha(theme.palette.background.paper, 1)}, ${alpha(theme.palette.background.paper, 0)})`
              : `linear-gradient(to right, ${alpha(theme.palette.background.default, 1)}, ${alpha(theme.palette.background.default, 0)})`,
            paddingRight: '20px',
            paddingLeft: '4px',
          }}
        >
          <IconButton
            size="small"
            onClick={scrollLeft}
            sx={{
              bgcolor: theme.palette.background.overlay,
              '&:hover': {
                bgcolor: theme.palette.background.overlayLight,
              }
            }}
          >
            <NavigateBeforeIcon />
          </IconButton>
        </Box>
      )}

      {/* Right scroll button */}
      {showRightArrow && (
        <Box
          sx={{
            position: 'absolute',
            right: -12,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            background: darkMode
              ? `linear-gradient(to left, ${alpha(theme.palette.background.paper, 1)}, ${alpha(theme.palette.background.paper, 0)})`
              : `linear-gradient(to left, ${alpha(theme.palette.background.default, 1)}, ${alpha(theme.palette.background.default, 0)})`,
            paddingLeft: '20px',
            paddingRight: '4px',
          }}
        >
          <IconButton
            size="small"
            onClick={scrollRight}
            sx={{
              bgcolor: theme.palette.background.overlay,
              '&:hover': {
                bgcolor: theme.palette.background.overlayLight,
              }
            }}
          >
            <NavigateNextIcon />
          </IconButton>
        </Box>
      )}

      {/* Filter container */}
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        {/* Scrollable filter container */}
        <Box
          ref={scrollContainerRef}
          sx={{
            display: 'flex',
            overflowX: 'auto',
            overflowY: 'hidden',
            padding: '8px 4px',
            scrollbarWidth: 'none', // Firefox
            '&::-webkit-scrollbar': { display: 'none' }, // Chrome, Safari, Edge
            '& > *:not(:last-child)': { mr: 1 },
            px: 2,
            flexGrow: 1,
          }}
          onScroll={checkScrollPosition}
        >
          {visibleFilterOptions.map((option) => (
            <Badge
              key={option.id}
              badgeContent={option.id === 'clear_filters' || filterCounts[option.id] <= 1 ? undefined : filterCounts[option.id]}
              color="default"
              sx={{
                '& .MuiBadge-badge': {
                  right: -3,
                  top: 3,
                  border: `2px solid ${theme.palette.background.paper}`,
                  padding: '0 4px',
                  minWidth: '20px',
                  fontWeight: 'bold',
                  fontSize: '0.7rem',
                  backgroundColor: option.color === 'default' || !filterCounts[option.id]
                    ? theme.palette.grey[500]
                    : theme.palette[option.color as 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success']?.main,
                  color: 'white',
                  transition: 'all 0.2s ease'
                }
              }}
            >
              <Chip
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {option.emoji && (
                        <Typography variant="body2" component="span" sx={{ mr: 0.5 }}>
                          {option.emoji}
                        </Typography>
                      )}
                      <Box component="span" sx={{ fontSize: '0.875rem' }}>
                        {option.label}
                      </Box>
                      {/* Show loading spinner inside the chip */}
                      {loadingMore[option.id] && (
                        <CircularProgress size={14} color="inherit" sx={{ ml: 1 }} />
                      )}
                    </Box>
                  }
                  clickable
                  disabled={loadingMore[option.id]}
                  color="default"
                  variant={selectedFilter === option.id ? 'filled' : 'outlined'}
                  onClick={(e) => {
                    if (option.id === 'clear_filters') {
                      handleClearFilters();
                    } else {
                      // First change the filter
                      onFilterChange(option.id);
                      // Then load more conversations
                      handleLoadMore(e as any, option.id);
                    }
                  }}
                  sx={{
                    fontWeight: selectedFilter === option.id ? 600 : 400,
                    px: 1,
                    py: 0.5,
                    transition: 'all 0.2s ease',
                    transform: selectedFilter === option.id ? 'scale(1.05)' : 'scale(1)',
                    m: 0.5,
                    height: 32,
                    bgcolor: selectedFilter === option.id 
                      ? (darkMode ? alpha(theme.palette.primary.main, 0.08) : alpha(theme.palette.primary.main, 0.08))
                      : 'transparent',
                    color: theme.palette.text.primary,
                    borderColor: selectedFilter === option.id 
                      ? theme.palette.primary.main
                      : (darkMode ? alpha(theme.palette.divider, 0.3) : theme.palette.divider),
                    boxShadow: selectedFilter === option.id
                      ? `0 0 0 1px ${alpha(theme.palette.primary.main, 0.5)}`
                      : 'none',
                    cursor: loadingMore[option.id] ? 'progress' : 'pointer',
                    '&:hover': {
                      transform: loadingMore[option.id] ? 'scale(1)' : 'scale(1.05)',
                      bgcolor: darkMode 
                        ? alpha(theme.palette.primary.main, 0.08)
                        : alpha(theme.palette.primary.main, 0.04),
                      borderColor: theme.palette.primary.main,
                    },
                    '&.Mui-disabled': {
                      opacity: 0.6,
                      color: theme.palette.text.secondary,
                    },
                    '& .MuiChip-label': {
                      display: 'flex',
                      alignItems: 'center',
                      color: theme.palette.text.primary,
                      px: 1,
                    }
                  }}
                />
              </Badge>
          ))}
        </Box>

        {/* Advanced filters button
        <Tooltip title="Advanced Filters" arrow>
          <Badge
            variant="dot"
            invisible={!isFilterActive}
            color="primary"
            sx={{ mr: 1 }}
          >
            <IconButton
              onClick={handleOpenAdvancedFilters}
              sx={{
                ml: 1,
                bgcolor: advancedFiltersOpen || isFilterActive ?
                  (darkMode ? alpha(theme.palette.primary.main, 0.15) : alpha(theme.palette.primary.main, 0.1)) :
                  'transparent',
                '&:hover': {
                  bgcolor: darkMode ? alpha(theme.palette.primary.main, 0.25) : alpha(theme.palette.primary.main, 0.2)
                },
                color: isFilterActive ? theme.palette.primary.main : 'inherit'
              }}
              aria-describedby={advancedFiltersId}
            >
              <FilterListIcon />
            </IconButton>
          </Badge>
        </Tooltip> */}
      </Box>

      {/* Advanced Filters Popover */}
      <Popover
        id={advancedFiltersId}
        open={advancedFiltersOpen}
        anchorEl={advancedFiltersAnchorEl}
        onClose={handleCloseAdvancedFilters}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            width: 400,
            p: 2,
            borderRadius: 2,
            boxShadow: 5,
          }
        }}
      >
        <Typography variant="h6" sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Advanced Filters
          <IconButton size="small" onClick={handleCloseAdvancedFilters}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Typography>

        <Grid container spacing={2}>
          {/* Status Filter */}
          <Grid item xs={12}>
            <Autocomplete
              id="status-filter"
              options={statusOptions}
              getOptionLabel={(option) => option}
              onChange={handleStatusSelect}
              value={advancedFilters.status || null}
              isOptionEqualToValue={(option, value) => option === value}
              fullWidth
              renderInput={(params) => (
                <TextField {...params} variant="outlined" size="small" label="Filter by Status" placeholder="Select Status" />
              )}
            />
          </Grid>

          {/* Query String */}
          <Grid item xs={12}>
            {isMobile ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Tooltip title={isSearchExpanded ? "Close search" : "Open search"} arrow>
                  <IconButton
                    onClick={() => setIsSearchExpanded(!isSearchExpanded)}
                    sx={{
                      bgcolor: isSearchExpanded ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                      color: isSearchExpanded ? theme.palette.primary.main : 'inherit',
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.2)
                      }
                    }}
                  >
                    <SearchIcon />
                  </IconButton>
                </Tooltip>
                <Collapse in={isSearchExpanded} orientation="horizontal" sx={{ width: '100%' }}>
                  <TextField
                    label="Search..."
                    variant="outlined"
                    fullWidth
                    size="small"
                    value={advancedFilters.queryString || ''}
                    onChange={(e) => handleQueryStringChange(e.target.value)}
                    placeholder="Search conversations..."
                    autoFocus={isSearchExpanded}
                    InputProps={{
                      endAdornment: advancedFilters.queryString ? (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleQueryStringChange('');
                            }}
                          >
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ) : null,
                    }}
                  />
                </Collapse>
              </Box>
            ) : (
              <TextField
                label="Search for something..."
                variant="outlined"
                fullWidth
                size="small"
                value={advancedFilters.queryString || ''}
                onChange={(e) => handleQueryStringChange(e.target.value)}
                placeholder="Search conversations..."
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: advancedFilters.queryString ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleQueryStringChange('');
                        }}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />
            )}
          </Grid>

          {/* Contact Filter */}
          <Grid item xs={12}>
            <ContactsSearchAutocomplete
              onSelectContact={(contact) => {
                setSelectedContactId(contact.id);
                handleAdvancedFilterChange('contactId', contact.id);
              }}
            />
          </Grid>

          {/* Channel Selection */}
          <Grid item xs={12}>
            <FormControl fullWidth size="small">
              <InputLabel>From Oppy</InputLabel>
              <Select
                value={advancedFilters.channel_id || ''}
                label="From Oppy"
                onChange={(e) => handleAdvancedFilterChange('channel_id', e.target.value)}
                displayEmpty
              >
                {channels.map((channel) => (
                  <MenuItem key={channel.id} value={channel.id}>
                    {channel?.config?.chatbot_name || channel.id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Communication Method Filter */}
          <Grid item xs={12}>
            <FormControl fullWidth size="small">
              <InputLabel>Communication Method</InputLabel>
              <Select
                value={advancedFilters.communication_method || ''}
                label="Communication Method"
                onChange={(e) => handleAdvancedFilterChange('communication_method', e.target.value)}
                displayEmpty
              >
                <MenuItem value="">
                  <em>All Methods</em>
                </MenuItem>
                <MenuItem value="email">📧 Email</MenuItem>
                <MenuItem value="sms">📱 SMS</MenuItem>
                <MenuItem value="chat">💬 Chat</MenuItem>
                <MenuItem value="voice">📞 Voice</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Date Filter Section */}
          <Grid item xs={12}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                cursor: 'pointer',
                py: 1
              }}
              onClick={() => setShowDateSelector(!showDateSelector)}
            >
              <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center' }}>
                <DateRangeIcon sx={{ mr: 1 }} /> Date Filters
              </Typography>
              {showDateSelector ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </Box>
            <Divider />

            <Collapse in={showDateSelector}>
              <Box sx={{ mt: 2 }}>
                <Grid container spacing={2}>
                  {/* Date Field Selection */}
                  <Grid item xs={12}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Date Field</InputLabel>
                      <Select
                        value={advancedFilters.dateField || ''}
                        label="Date Field"
                        onChange={(e) => handleAdvancedFilterChange('dateField', e.target.value)}
                      >
                        {DATE_FIELD_OPTIONS.map(option => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Time Preset Selection */}
                  <Grid item xs={12}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Time Period</InputLabel>
                      <Select
                        value={advancedFilters.timePreset || timeFilter}
                        label="Time Period"
                        onChange={(e) => {
                          handleTimePresetChange(e.target.value as string);
                          handleTimeFilterChange(e);
                        }}
                        disabled={!advancedFilters.dateField}
                      >
                        <MenuItem value="">
                          <em>No time filter</em>
                        </MenuItem>
                        <ListSubheader>Recent Periods</ListSubheader>
                        <MenuItem value="24h">Past 24 Hours</MenuItem>
                        <MenuItem value="7d">Past 7 Days</MenuItem>
                        <MenuItem value="30d">Past 30 Days</MenuItem>
                        <MenuItem value="60d">Past 60 Days</MenuItem>
                        <MenuItem value="90d">Past 90 Days</MenuItem>
                        <ListSubheader>Monthly</ListSubheader>
                        {getMonthOptions().map(month => (
                          <MenuItem key={month.value} value={month.value}>
                            {month.label}
                          </MenuItem>
                        ))}
                        <MenuItem value="custom">Custom Date Range</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Custom Date Range */}
                  {advancedFilters.timePreset === 'custom' && (
                    <Grid item xs={12}>
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <DatePicker
                              label="Start Date"
                              value={advancedFilters.customDateStart}
                              onChange={(date) => handleAdvancedFilterChange('customDateStart', date)}
                              slotProps={{ textField: { size: 'small', fullWidth: true } }}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <DatePicker
                              label="End Date"
                              value={advancedFilters.customDateEnd}
                              onChange={(date) => handleAdvancedFilterChange('customDateEnd', date)}
                              slotProps={{ textField: { size: 'small', fullWidth: true } }}
                            />
                          </Grid>
                        </Grid>
                      </LocalizationProvider>
                    </Grid>
                  )}
                </Grid>
              </Box>
            </Collapse>
          </Grid>

          {/* Limit Controls */}
          <Grid item xs={12}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                cursor: 'pointer',
                py: 1
              }}
            >
              <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center' }}>
                <SettingsIcon sx={{ mr: 1 }} /> Results Settings
              </Typography>
            </Box>
            <Divider />

            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Results Limit</InputLabel>
                  <Select
                    value={advancedFilters.limit || limit}
                    onChange={(e) => handleAdvancedFilterChange('limit', Number(e.target.value))}
                    label="Results Limit"
                  >
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={25}>25</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                    <MenuItem value={100}>100</MenuItem>
                    <MenuItem value={250}>250</MenuItem>
                    <MenuItem value={500}>500</MenuItem>
                    <MenuItem value={1000}>1000</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Messages per Conversation</InputLabel>
                  <Select
                    value={advancedFilters.messagesLimit || messagesLimit}
                    onChange={(e) => handleAdvancedFilterChange('messagesLimit', Number(e.target.value))}
                    label="Messages per Conversation"
                  >
                    <MenuItem value={1}>1 message</MenuItem>
                    <MenuItem value={5}>5 messages</MenuItem>
                    <MenuItem value={10}>10 messages</MenuItem>
                    <MenuItem value={20}>20 messages</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Grid>

          {/* Toggle Filters */}
          <Grid item xs={12} sx={{ mt: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={advancedFilters.user_not_responded_to || false}
                  onChange={(e) => handleAdvancedFilterChange('user_not_responded_to', e.target.checked)}
                  color="primary"
                />
              }
              label="Only show conversations with no user response"
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={advancedFilters.has_draft_messages || (advancedFilters.status === "Has Draft Messages (Pending Delivery)")}
                  onChange={(e) => handleAdvancedFilterChange('has_draft_messages', e.target.checked)}
                  color="primary"
                />
              }
              label="Show conversations with draft messages (pending delivery)"
            />
          </Grid>

          {/* Action Buttons */}
          <Grid item xs={12} sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button
              variant="outlined"
              color="inherit"
              onClick={handleClearFilters}
              startIcon={<ClearIcon />}
            >
              Clear
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={applyAdvancedFilters}
              startIcon={<SearchIcon />}
            >
              Apply Filters
            </Button>
          </Grid>
        </Grid>
      </Popover>
    </Box>
  );
};

export default ConversationFilters; 
